import { useState } from 'react';
import { ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, Boxes, CalendarDays, ClipboardList, Clock, FileText, History, Inbox, MapPin, PackageCheck, PackageMinus, PackageOpen, Search, TriangleAlert, User } from 'lucide-react';
import type {
  RequisitionDto,
  SubdivisionMovementRow,
  SubdivisionStockRow,
  Unit,
} from '@eombor/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { ApiErrorState } from '../../../shared/ui/ApiErrorState.js';
import { t } from '../../../shared/i18n/index.js';
import { requisitionStatusText, unitText } from '../../../shared/i18n/labels.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import { requisitionStatusTone } from '../../requisition/model/requisition-status.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { RequirementDialog } from '../../accounting/ui/RequirementDialog.js';
import { ReceiptDialog } from '../../handover/ui/ReceiptDialog.js';
import { MyWriteOffsView } from '../../writeoff/ui/MyWriteOffsView.js';
import { WriteOffDialog } from '../../writeoff/ui/WriteOffDialog.js';
import {
  mySubdivisionKeys,
  useMyMovements,
  useMyRequisitions,
  useMyStock,
  useMyStorerooms,
  useMySubdivision,
  usePendingReceipts,
} from '../model/subdivision.queries.js';
import { MoveLotDialog, placeText } from './MoveLotDialog.js';
import { storekeepingApi } from '../../storekeeping/api/storekeeping.api.js';
import { LabelPrintButton } from '../../storekeeping/ui/LabelPrintButton.js';
import { TeamView } from '../../storekeeping/ui/TeamView.js';
import { UsageHistoryView } from '../../storekeeping/ui/UsageHistoryView.js';
import { LotUsersDialog } from '../../storekeeping/ui/LotUsersDialog.js';
import { StoreroomStrip } from './StoreroomStrip.js';
import { OpeningDialog } from './OpeningDialog.js';
import { OpeningsView } from './OpeningsView.js';
import { StockStatsBand } from './StockStatsBand.js';
import {
  DEFAULT_MY_WAREHOUSE_TAB,
  type MyWarehouseTab,
} from '../model/my-warehouse-search.js';

/**
 * «MENING OMBORIM» — bo'lim xodimining O'Z kichik ombori.
 *
 * Bu ekran boshqaruv ekrani emas: bu yerda yaratish, tahrirlash yoki boshqa
 * bo'limni ko'rish yo'q. Xodim bu yerga bitta savol bilan keladi — «bizda
 * nima bor?» — va javob birinchi ekranda, aylantirmasdan ko'rinishi kerak.
 *
 * Shuning uchun ikkita ko'rinish: QOLDIQ («hozir nima bor») va JURNAL («nima
 * kelgan»). Ular bitta jadvalga sig'maydi: nolga tushgan mahsulot qoldiqdan
 * chiqib ketadi, jurnalda esa qoladi — «o'tgan oy nima olgandik?» degan
 * savolga javob faqat o'sha yerda.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Miqdor `numeric(14,3)` — uchdan ortiq kasr bazada baribir yo'qoladi. */
const qtyText = (qty: number): string => (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

/**
 * Kirim TURI — rang ma'no tashiydi, bezak emas.
 *
 * Kirim yashil (bo'limga qo'shildi), chiqim turlari esa ogohlantiruvchi
 * ranglarda: jurnalga qaragan odam «nima keldi» va «nima ketdi» ni satrni
 * o'qimasdan ajratishi kerak. 2-bosqichdagi turlar ham shu yerda ATAYLAB
 * sanab o'tilgan — ular jadvalda allaqachon bor va yangi tur qo'shilganda
 * kompilyator shu joyni ko'rsatadi.
 */
const MOVEMENT_TONE: Record<SubdivisionMovementRow['type'], 'success' | 'warn' | 'danger' | 'info'> =
  {
    IN: 'success',
    TRANSFER_IN: 'success',
    WRITE_OFF: 'danger',
    RETURN: 'warn',
    TRANSFER_OUT: 'info',
    MOVE: 'info',
    USE: 'warn',
    /* Yozuvning O'ZI xato bo'lgan — boshlang'ich qoldiq bekor qilindi.
       Mol qayoqqa ham ketmagan, shuning uchun bu «chiqim» emas. */
    CORRECTION: 'danger',
  };

type Tab = MyWarehouseTab;

export interface MyWarehousePageProps {
  /**
   * Talabnoma kartochkasini ochadi.
   *
   * Marshrutlash ILOVA QATLAMIDA qoladi (`routes/requisitions.tsx` naqshi):
   * bo'linma xususiyati router ni bilmasa, uni routersiz sinash mumkin va
   * havola manzili bitta joyda — marshrutlar faylida — turadi.
   */
  onOpenRequisition?: (id: string) => void;
  /**
   * Ochiq bo'lim — MANZILDAN keladi, shuning uchun yangilash uni
   * yo'qotmaydi. Berilmasa sahifa o'zi boshqaradi (sinovlar uchun).
   */
  tab?: MyWarehouseTab;
  onTabChange?: (tab: MyWarehouseTab) => void;
}

export const MyWarehousePage = ({ onOpenRequisition, tab: tabProp, onTabChange }: MyWarehousePageProps = {}) => {
  const [ownTab, setOwnTab] = useState<Tab>(DEFAULT_MY_WAREHOUSE_TAB);
  const setTab = (next: Tab): void => {
    setOwnTab(next);
    onTabChange?.(next);
  };
  const [search, setSearch] = useState('');
  /* Tanlangan ombor — tab almashganda ham esda qoladi. */
  const [storeroomId, setStoreroomId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const mine = useMySubdivision();
  const { canAnywhere } = usePermission();
  const canManageRooms = canAnywhere('storeroom.manage');
  const canSeeUsage = canManageRooms || canAnywhere('storeroom.usage.read');
  const canImportOpening = canAnywhere('subdivision.stock.import');

  /* Manzildagi bo'lim ruxsatdan O'TISHI kerak: havolani boshqa odamga
     yuborish mumkin, va ruxsati yo'q odam tugmasi yo'q bo'limda
     qolib ketmasin. */
  const allowed = (candidate: Tab): boolean =>
    (candidate !== 'usage' || canSeeUsage) &&
    (candidate !== 'team' || canManageRooms) &&
    (candidate !== 'opening' || canImportOpening);
  const wanted = tabProp ?? ownTab;
  const tab = allowed(wanted) ? wanted : DEFAULT_MY_WAREHOUSE_TAB;
  const pending = usePendingReceipts();
  const pendingCount = pending.data?.total ?? 0;

  if (mine.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  /*
   * Bo'lim biriktirilmagan xodim — XATO emas, oddiy hol (skladchi, buxgalter,
   * superadmin). Ekran shuning uchun qizil xato ko'rsatmaydi: u nima
   * bo'lganini va KIMGA murojaat qilishni aytadi.
   */
  if (mine.isError) {
    /*
     * Ro'yxat, filtr va qidiruv bu shoxda UMUMAN chizilmaydi: bo'lim
     * yo'q ekan, ularning ortida hech qanday ma'lumot yo'q va ular
     * bo'sh va'da bo'lardi.
     *
     * Xabar matni serverdan keladi («kadrlar bo'limiga murojaat
     * qiling») — u umumiy jumladan ko'ra aniqroq aytadi. Tarmoq
     * uzilishi yoki 500 bo'lsa, `ApiErrorState` boshqa matn tanlaydi.
     */
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={t('myStock.title')} description={t('myStock.subtitle')} />
        <ApiErrorState
          error={mine.error}
          title={t('myStock.noSubdivision')}
          onRetry={() => void mine.refetch()}
        />
      </div>
    );
  }

  const subdivision = mine.data;

  return (
    <div className="flex flex-col gap-5">
      {/*
        SARLAVHA PANELI — bo'limning «pasporti».
        Nom yolg'iz turmaydi: «R va AX» bir necha korxonada bor va egasi
        ko'rsatilmasa, xodim o'z bo'limini ko'rayotganiga ishonch hosil
        qila olmasdi.
      */}
      <section
        className={cn(
          'relative overflow-hidden rounded-[var(--radius-lg)] border border-border',
          'bg-surface p-5 shadow-[var(--shadow-1)]',
        )}
      >
        {/* Yumshoq fon — urg'u rangining bir tomchisi. Panel butunlay
            bo'yalsa, ustidagi raqamlar o'qilishini yo'qotardi. */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute -top-24 -right-16 size-64 rounded-full',
            'bg-accent-soft opacity-70 blur-2xl',
          )}
        />

        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="flex min-w-0 items-start gap-4">
            <span
              aria-hidden
              className={cn(
                'flex size-14 shrink-0 items-center justify-center rounded-[var(--radius-md)]',
                'bg-accent text-[var(--color-accent-contrast)] shadow-[var(--shadow-1)]',
              )}
            >
              <PackageOpen size={26} />
            </span>

            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl font-semibold tracking-tight text-text">
                  {subdivision.name}
                </h1>
                <Badge tone="accent" mono>
                  {subdivision.code}
                </Badge>
                {subdivision.isActive ? null : (
                  <Badge tone="neutral">{t('myStock.inactive')}</Badge>
                )}
              </span>
              <p className="truncate text-sm text-text-muted">{subdivision.orgUnitName}</p>
            </div>
          </div>

          {/*
            KO'RSATKICHLAR SARLAVHADAN CHIQDI.
            
            Ilgari bu yerda ikkita karta turardi: «Nomlar» va «Oxirgi
            kirim». Ular endi pastdagi panelda va u yerda ancha ko'proq
            narsa aytadi — sana yoniga nechta kirim bo'lgani, nomlar
            yoniga esa nechtasi kam qolgani qo'shildi. Sarlavhada
            takrorlansa, bir xil son ikki joyda turib, ikkovi ham
            e'tiborni yo'qotardi.
          */}
          <div className="flex flex-col items-end gap-3">
            {/*
              RASXOD — panelda BO'LIMGA havola, amalning o'zi emas.

              Amal qoldiq kartochkasida turadi va turishi ham kerak:
              rasxod har doim aniq bir PARTIYAdan qilinadi, panelda esa
              qaysi mahsulot haqida gap ketayotgani ma'lum emas. Bu yerdagi
              tugma xodimni yuborgan aktlari ro'yxatiga olib boradi —
              rad etilgani bo'lsa, u aynan shu yerdan tuzatiladi.
            */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/*
                ESKI QOLDIQ — panelda AMALNING O'ZI, bo'limga havola emas.

                U bir martalik ish: xodim bo'limni ochgani zahoti «qayerdan
                boshlayman» degan savolga javob shu tugma bo'lishi kerak.
                Bo'lim esa kiritilganlarni KEYIN ko'rish uchun.
              */}
              {canImportOpening ? (
                <Button variant="secondary" onClick={() => setImporting(true)}>
                  <ArrowDownToLine size={16} aria-hidden />
                  {t('opening.open')}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => setTab('writeoffs')}>
                <ArrowUpFromLine size={16} aria-hidden />
                {t('myStock.spend')}
              </Button>
            </div>
          </div>
        </div>

        {/*
          QABUL KUTAYOTGANLAR — panelning pastida, butun kenglikda.
          Mahsulot omborni tark etgan, lekin bo'linma tasdiqlamagan: qoldiqda
          u YO'Q va xodim uni ro'yxatdan izlab topa olmasdi. Ogohlantirish
          shu bo'shliqni yopadi va darhol kerakli ko'rinishga olib o'tadi.
          Nol bo'lsa panel umuman chizilmaydi — "0 ta kutmoqda" e'tiborni
          tortadi, lekin hech narsa aytmaydi.
        */}
        {pendingCount === 0 ? null : (
          <button
            type="button"
            onClick={() => setTab('requisitions')}
            className={cn(
              'relative mt-4 flex w-full flex-wrap items-center gap-3 rounded-[var(--radius-md)]',
              'border border-warn bg-warn-soft px-4 py-3 text-left',
              'transition-colors duration-[var(--motion-fast)] hover:border-warn hover:brightness-[0.98]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            <TriangleAlert size={18} aria-hidden className="shrink-0 text-warn" />
            <span className="min-w-0 flex-1 text-sm font-medium text-text">
              {t('myStock.pendingReceipt', { count: pendingCount })}
            </span>
            <span className="text-xs text-text-muted">{t('myStock.pendingReceiptHint')}</span>
          </button>
        )}
      </section>

      {/*
        KO'RSATKICHLAR faqat QOLDIQ ko'rinishida.
        
        Boshqa bo'limlar (jurnal, talabnomalar, rasxodlar) o'z savoliga
        ega va tepada turgan «nima tugab qolyapti» ular bilan bog'liq
        emas — u yerda panel shunchaki ekranni pastga surib yuborardi.
      */}
      {tab === 'stock' ? <StockStatsBand /> : null}

      {tab === 'stock' ? <StoreroomStrip selectedId={storeroomId} onSelect={setStoreroomId} /> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          label={t('myStock.viewLabel')}
          value={tab}
          onChange={(next) => setTab(next as Tab)}
          options={[
            { value: 'stock', label: t('myStock.tabStock') },
            /* Talabnomalar QOLDIQ bilan JURNAL o'rtasida turadi: u ikkalasini
               bog'laydigan hujjat — «nima so'radik» va «nima kelib tushdi». */
            { value: 'requisitions', label: t('myStock.tabRequisitions') },
            /* Rasxodlar JURNALDAN oldin: jurnal o'tmish, rasxod esa
               hozir javob kutayotgan ish — rad etilgan aktni xodim
               tuzatib qayta yuborishi kerak. */
            { value: 'writeoffs', label: t('myStock.tabWriteOffs') },
            /* Tarix va jamoa — faqat kerakli ruxsati borga: bo'sh yoki 403
               qaytaradigan ko'rinish tugma bo'lib turmasin. */
            ...(canSeeUsage ? [{ value: 'usage', label: t('myStock.tabUsage') }] : []),
            ...(canManageRooms ? [{ value: 'team', label: t('myStock.tabTeam') }] : []),
            /* Eski qoldiq — JURNALDAN oldin: u ham o'tmish, lekin
               tuzatilishi mumkin bo'lgan o'tmish. */
            ...(canImportOpening ? [{ value: 'opening', label: t('opening.tab') }] : []),
            { value: 'journal', label: t('myStock.tabJournal') },
          ]}
        />

        {/* Qidiruv faqat qoldiqda: jurnalda odam sanaga qarab yuradi, nom
            bo'yicha qidiruv u yerda boshqa savolga javob berardi. */}
        {tab === 'stock' ? (
          <Input
            variant="filter"
            type="search"
            aria-label={t('myStock.search')}
            placeholder={t('myStock.search')}
            prefix={<Search size={16} />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        ) : null}
      </div>

      {tab === 'stock' ? (
        <StockView
          search={search}
          storeroomId={storeroomId}
          {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })}
        />
      ) : tab === 'requisitions' ? (
        <RequisitionsView {...(onOpenRequisition === undefined ? {} : { onOpen: onOpenRequisition })} />
      ) : tab === 'writeoffs' ? (
        <MyWriteOffsView />
      ) : tab === 'usage' ? (
        <UsageHistoryView {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })} />
      ) : tab === 'team' ? (
        <TeamView />
      ) : tab === 'opening' ? (
        <OpeningsView onAdd={() => setImporting(true)} />
      ) : (
        <JournalView {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })} />
      )}

      {importing ? <OpeningDialog onClose={() => setImporting(false)} /> : null}
    </div>
  );
};

const StockView = ({
  search,
  storeroomId,
  onOpenRequisition,
}: {
  search: string;
  storeroomId: string | null;
  onOpenRequisition?: (id: string) => void;
}) => {
  const [moveFor, setMoveFor] = useState<SubdivisionStockRow | null>(null);
  const rooms = useMyStorerooms();
  const { canAnywhere } = usePermission();
  /*
   * Rasxod tugmasi RUXSAT bo'lgandagina chiziladi.
   *
   * Ilgari u hammaga ko'rinardi va bu eng qimmat turdagi to'siq edi:
   * xodim miqdorni yozadi, sababni yozadi, akt qog'ozini va suratlarni
   * yuklaydi, ikkita tasdiq belgisini bosadi — va shundan keyingina
   * server «bu amal sizniki emas» deb qaytarardi. Berilmagan amalni
   * boshlashga umuman yo'l qo'ymaslik kerak.
   */
  const canWriteOff = canAnywhere('writeoff.create');
  /* «Kim ishlatgan» — ishlatish tarixini ko'rish huquqi bilan bir xil
     ma'lumot, faqat bitta lot bo'yicha saralangani. Shuning uchun to'siq
     ham o'sha: `storeroom.manage` yoki `storeroom.usage.read`. */
  const canSeeUsers = canAnywhere('storeroom.manage') || canAnywhere('storeroom.usage.read');
  /* Ochilgan lot — ro'yxatning O'ZIDA: har kartochka o'z oynasini olib
     yursa, ochilmagan o'nlab oyna daraxtda turaverardi. */
  const [usersFor, setUsersFor] = useState<SubdivisionStockRow | null>(null);
  /* Ko'chirish faqat MAS'UL uchun — tugma o'shanda chiziladi. */
  const canMove = (row: SubdivisionStockRow): boolean =>
    rooms.data?.some((room) => room.id === row.storeroomId && room.canManage) === true;
  /* TREBOVANIYA qoldiqda ham kerak: xodim mahsulotni qo'lida ushlab turib
     «bu qaysi qog'oz bilan kelgan?» deb so'raydi va javob ro'yxatni
     almashtirmasdan, shu yerda bo'lishi kerak. */
  const [requirementFor, setRequirementFor] = useState<RequirementTarget | null>(null);
  /* Rasxod oynasi ochilgan SATR. Kartochkaning o'zida saqlansa, har
     kartochka o'z oynasini olib yurardi va ochilmagan o'nlab oyna
     daraxtda turaverardi. */
  const [writeOffFor, setWriteOffFor] = useState<SubdivisionStockRow | null>(null);
  const stock = useMyStock(search, storeroomId);

  if (stock.isPending) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (stock.isError) {
    return (
      <Card>
        <EmptyState
          icon={<TriangleAlert size={20} aria-hidden />}
          title={t('myStock.loadError')}
          description={stock.error.message}
        />
      </Card>
    );
  }

  if (stock.data.items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Inbox size={20} aria-hidden />}
          title={search === '' ? (storeroomId === null ? t('myStock.emptyTitle') : t('storeroom.empty')) : t('myStock.notFound')}
          description={search === '' && storeroomId === null ? t('myStock.emptyHint') : undefined}
        />
      </Card>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {groupByRequisition(stock.data.items).map((group) => (
          <StockGroup
            key={group.key}
            group={group}
            onShowRequirement={setRequirementFor}
            {...(canWriteOff ? { onWriteOff: setWriteOffFor } : {})}
            {...(canSeeUsers ? { onShowUsers: setUsersFor } : {})}
            onMove={setMoveFor}
            canMove={canMove}
            {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })}
          />
        ))}
      </div>

      {requirementFor === null ? null : (
        <RequirementFor target={requirementFor} onClose={() => setRequirementFor(null)} />
      )}

      {writeOffFor === null ? null : (
        <WriteOffDialog open row={writeOffFor} onClose={() => setWriteOffFor(null)} />
      )}

      {usersFor === null ? null : (
        <LotUsersDialog row={usersFor} onClose={() => setUsersFor(null)} />
      )}

      {moveFor === null ? null : <MoveLotDialog row={moveFor} onClose={() => setMoveFor(null)} />}
    </>
  );
};

/**
 * Bitta QOG'OZ bilan kelgan mahsulotlar — bitta guruh.
 *
 * Talabnomasi yo'q satr o'ziga alohida guruh bo'ladi: aks holda ekranda ikki
 * xil ritm paydo bo'lardi — bir qismi qutida, bir qismi yalang'och.
 */
interface StockGroupRow {
  key: string;
  /** Quti burchagidagi yozuv: talabnoma raqami yoki (bo'lmasa) partiya raqami. */
  label: string;
  requisitionId: string | null;
  requisitionDocNo: string | null;
  items: SubdivisionStockRow[];
}

/**
 * Qoldiq satrlarini TALABNOMA bo'yicha yig'adi.
 *
 * Tartib serverdan kelgan holicha qoladi (mahsulot nomi bo'yicha): guruh
 * o'zining BIRINCHI satri qayerda uchrasa, o'sha joyda turadi. Shu tufayli
 * qidiruv paytida ro'yxat sakramaydi — filtr satrlarni olib tashlaydi,
 * qolganlarining o'zaro tartibini o'zgartirmaydi.
 *
 * Guruhlash mijozda: `useMyStock` bir sahifada 200 satr oladi, ya'ni bitta
 * talabnomaning satrlari sahifalar orasida bo'linib ketmaydi.
 */
const groupByRequisition = (rows: readonly SubdivisionStockRow[]): StockGroupRow[] => {
  const groups: StockGroupRow[] = [];
  const byDocNo = new Map<string, StockGroupRow>();

  for (const row of rows) {
    if (row.requisitionDocNo === null) {
      groups.push({
        key: `batch:${row.batchId}`,
        label: row.batchNo,
        requisitionId: null,
        requisitionDocNo: null,
        items: [row],
      });
      continue;
    }

    const existing = byDocNo.get(row.requisitionDocNo);
    if (existing !== undefined) {
      existing.items.push(row);
      continue;
    }

    const group: StockGroupRow = {
      key: `req:${row.requisitionDocNo}`,
      label: row.requisitionDocNo,
      requisitionId: row.requisitionId,
      requisitionDocNo: row.requisitionDocNo,
      items: [row],
    };
    byDocNo.set(row.requisitionDocNo, group);
    groups.push(group);
  }

  return groups;
};

/**
 * QUTI — bitta talabnoma va uning ichidagi mahsulotlar.
 *
 * Talabnoma raqami va «Trebovaniya» kartochkadan SHU YERGA ko'chdi. Ilgari
 * ular har satrda takrorlanardi: bitta qog'oz bilan uchta mahsulot kelsa,
 * bir xil `A-` raqami uch marta chiqib, ekranga qarab «bu bitta qog'ozmi
 * yoki uchtami?» degan savol tug'ilardi. Endi javob ko'rinishning O'ZIDA —
 * bitta qog'oz, bitta quti.
 *
 * `aria-label` quti raqami: ekran o'quvchi ro'yxatga kirganda avval qaysi
 * hujjat haqida gap ketayotganini aytadi, keyin mahsulotlarni sanaydi.
 */
const StockGroup = ({
  group,
  onOpenRequisition,
  onShowRequirement,
  onWriteOff,
  onShowUsers,
  onMove,
  canMove,
}: {
  group: StockGroupRow;
  onOpenRequisition?: (id: string) => void;
  onShowRequirement: (target: RequirementTarget) => void;
  onWriteOff?: (row: SubdivisionStockRow) => void;
  onShowUsers?: (row: SubdivisionStockRow) => void;
  onMove: (row: SubdivisionStockRow) => void;
  canMove: (row: SubdivisionStockRow) => boolean;
}) => (
  <section
    aria-label={group.label}
    className={cn(
      'flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border',
      'bg-surface-2 p-3 sm:p-4',
    )}
  >
    {/* Hujjat — O'NG burchakda. Chap tomon ataylab bo'sh: mahsulot nomlari
        allaqachon quyida turadi va tepaga ikkinchi sarlavha qo'yilsa, quti
        o'zi ko'rsatayotgan narsadan ko'ra ko'proq joy egallardi. */}
    <header className="flex flex-wrap items-center justify-end gap-2">
      {group.requisitionDocNo === null || group.requisitionId === null ? (
        <Badge tone="neutral" mono>
          {group.label}
        </Badge>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              onShowRequirement({ id: group.requisitionId as string, docNo: group.requisitionDocNo as string })
            }
          >
            <FileText size={14} aria-hidden />
            {t('myStock.reqRequirement')}
          </Button>
          <RequisitionRef
            docNo={group.requisitionDocNo}
            id={group.requisitionId}
            {...(onOpenRequisition === undefined ? {} : { onOpen: onOpenRequisition })}
          />
        </>
      )}
    </header>

    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {group.items.map((row) => (
        <StockCard
          key={row.lotId}
          row={row}
          {...(onWriteOff === undefined ? {} : { onWriteOff })}
          {...(onShowUsers === undefined ? {} : { onShowUsers })}
          {...(canMove(row) ? { onMove } : {})}
        />
      ))}
    </ul>
  </section>
);

/**
 * Qoldiqning bitta satri — KARTOCHKA, jadval qatori emas.
 *
 * Sabab telefonda: bu ekran ko'pincha bo'lim xodimining qo'lida ochiladi va
 * olti ustunli jadval u yerda yon tomonga sirg'aladi. Kartochkada esa eng
 * muhim raqam — MIQDOR — birinchi qarashda ko'rinadi, kelib chiqishi esa
 * ostida qoladi.
 */
const StockCard = ({
  row,
  onWriteOff,
  onShowUsers,
  onMove,
}: {
  row: SubdivisionStockRow;
  onWriteOff?: (row: SubdivisionStockRow) => void;
  onShowUsers?: (row: SubdivisionStockRow) => void;
  /** Faqat omborning mas'uliga beriladi. */
  onMove?: (row: SubdivisionStockRow) => void;
}) => {
  const available = Math.max(0, Math.round((row.qty - row.pendingWriteOffQty) * 1000) / 1000);

  return (
  <li
    className={cn(
      'flex flex-col gap-3 rounded-[var(--radius-md)] border border-border',
      'bg-surface p-4 shadow-[var(--shadow-1)]',
      'transition-[border-color] duration-[var(--motion-fast)] hover:border-border-strong',
    )}
  >
    {/* Kartochkada endi FAQAT mahsulot: hujjat raqami ham, «Trebovaniya» ham
        qutining tepasiga ko'chdi va butun guruh uchun bir marta turadi. */}
    <span className="flex items-start justify-between gap-2">
      <span className="min-w-0 text-sm leading-5 font-medium text-pretty text-text">
        {row.nomenclatureName}
      </span>
      {/* ETIKETKA KODI — javondagi yorliq bilan ekranni bog'laydigan yagona belgi. */}
      <span
        title={t('storeroom.code')}
        className="tabular shrink-0 rounded-[var(--radius-sm)] border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem] tracking-wide text-text-muted"
      >
        {row.code}
      </span>
    </span>

    {/* JOYI — «qayerdan olaman?» savoliga javob, raqamdan oldin. */}
    <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-soft px-2.5 py-1 text-xs font-medium text-accent">
      <MapPin size={12} aria-hidden />
      {placeText(row)}
    </span>

    <p className="flex flex-wrap items-baseline gap-1.5">
      <span className="tabular text-2xl leading-7 font-semibold text-text">{qtyText(row.qty)}</span>
      <span className="text-sm text-text-muted">{unitText(row.unit as Unit)}</span>
      {/* JAVONDA — mobil ilovada olinganidan keyin qolgani. Hisobdagidan
          farq qilsagina ko'rsatiladi: farq = ishlatilgan, rasxodi yozilmagan. */}
      {row.shelfQty < row.qty ? (
        <span className="tabular rounded-full bg-warn-soft px-2 py-0.5 text-xs font-medium text-warn">
          {t('myStock.onShelf', { qty: qtyText(row.shelfQty) })}
        </span>
      ) : null}
      {/* Bandlik AYNAN raqam yonida: "3 dona bor edi, nega yubora
          olmayapman" degan savol aynan shu joyda tug'iladi. */}
      {row.pendingWriteOffQty > 0 ? (
        <span className="tabular text-xs text-warn">
          {t('myStock.pendingWriteOff', { qty: qtyText(row.pendingWriteOffQty) })}
        </span>
      ) : null}
    </p>

    {/* KELIB CHIQISHI — qachon, qaysi partiya, kim qabul qilgan.
        Ma'lumot qoldiq bilan BIRGA keladi (jurnaldagi oxirgi kirimdan),
        ya'ni har satr uchun qo'shimcha so'rov yo'q.

        Partiya raqami faqat talabnomali guruhda ko'rsatiladi: talabnomasiz
        guruhda u allaqachon quti burchagida turadi. */}
    <dl className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2.5 text-xs text-text-muted">
      <Fact icon={<CalendarDays size={12} />} value={formatDate(row.receivedAt)} />
      {row.requisitionDocNo === null ? null : (
        <Fact icon={<Boxes size={12} />} value={row.batchNo} mono />
      )}
      {row.receivedByName === null ? null : (
        <Fact icon={<User size={12} />} value={row.receivedByName} />
      )}
    </dl>

    {/* RASXOD — kartochkadagi yagona harakat.
        Mavjud miqdor nolga tushsa tugma o'chiriladi va SABABI yonida
        turadi: o'chirilgan tugma yolg'iz tursa, xodim "ishlamayapti"
        deb o'ylaydi (`IssueStep` dagi bilan ayni qoida). */}
    <div className="flex items-center justify-between gap-2 border-t border-border pt-2.5">
      {available === 0 ? (
        <span className="text-xs text-text-muted">{t('myStock.writeOffBlocked')}</span>
      ) : (
        <span />
      )}
      <span className="flex items-center gap-1">
        {/* «Kim ishlatgan» faqat ROSTDAN olingan bo'lsa chiziladi: hech
            kim olmagan lotda tugma bo'sh oyna ochardi va odam uni bekorga
            bosardi. Farq `qty - shelfQty` — javondan ketgan miqdor. */}
        {onShowUsers === undefined || row.qty - row.shelfQty <= 0 ? null : (
          <Button variant="ghost" size="sm" onClick={() => onShowUsers(row)}>
            <History size={14} aria-hidden />
            {t('lotUsers.open')}
          </Button>
        )}
        <LabelPrintButton load={() => storekeepingApi.lotLabel(row.lotId)} />
        {onMove === undefined ? null : (
          <Button variant="ghost" size="sm" onClick={() => onMove(row)}>
            <ArrowRightLeft size={14} aria-hidden />
            {t('storeroom.move')}
          </Button>
        )}
        {onWriteOff === undefined ? null : (
          <Button variant="ghost" size="sm" disabled={available === 0} onClick={() => onWriteOff(row)}>
            <PackageMinus size={14} aria-hidden />
            {t('myStock.writeOff')}
          </Button>
        )}
      </span>
    </div>
  </li>
  );
};

/**
 * TALABNOMA RAQAMI — hujjatga HAVOLA, ma'lumot bo'lagi emas.
 *
 * Ilgari u sana va ism qatorida, mayda kulrang matn bo'lib turardi va
 * kartochkadagi eng oxirgi narsa edi. Amalda esa odam mahsulotni aynan shu
 * raqam bilan chaqiradi: «A-2026-0014 keldimi?» — partiya raqami bilan emas.
 * Shuning uchun u endi tepada, urg'u bilan va BOSILADI.
 *
 * `id` yoki `onOpen` bo'lmasa oddiy yozuvga aylanadi: bosilmaydigan narsa
 * bosiladigandek ko'rinmasligi kerak.
 */
const RequisitionRef = ({
  docNo,
  id,
  onOpen,
}: {
  docNo: string;
  id: string | null;
  onOpen?: (id: string) => void;
}) => {
  const body = (
    <>
      <FileText size={13} aria-hidden />
      <span className="tabular">{docNo}</span>
    </>
  );
  const shape = cn(
    'inline-flex shrink-0 items-center gap-1.5 rounded-[var(--radius-sm)]',
    'bg-accent-soft px-2 py-1 text-xs font-medium text-accent',
  );

  if (id === null || onOpen === undefined) {
    return <span className={shape}>{body}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      title={t('myStock.openRequisition', { docNo })}
      className={cn(
        shape,
        'transition-colors duration-[var(--motion-fast)] hover:bg-accent hover:text-[var(--color-accent-contrast)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      )}
    >
      {body}
    </button>
  );
};

const Fact = ({
  icon,
  value,
  mono = false,
}: {
  icon: React.ReactNode;
  value: string;
  mono?: boolean;
}) => (
  <span className={cn('inline-flex items-center gap-1.5', mono ? 'tabular' : undefined)}>
    <span aria-hidden className="text-text-faint">
      {icon}
    </span>
    {value}
  </span>
);


/**
 * BO'LINMANING TALABNOMALARI — «nima so'radik va u qayerda?».
 *
 * Jurnaldan farqi NIYAT bilan FAKT o'rtasida: jurnalda faqat omborga
 * KIRGAN mahsulot bor, bu yerda esa hali yo'ldagilar ham — tasdiq
 * kutayotgan, olib ketilmagan, rad etilgan. Bo'lim xodimining eng ko'p
 * beradigan savoli aynan shular haqida: «o'tgan haftagi talabnoma nima
 * bo'ldi?»
 *
 * Kartochka — jadval emas: telefonda oltita ustun yon tomonga sirg'aladi,
 * bu yerda esa eng muhim ikkitasi — RAQAM va HOLAT — birinchi qatorda
 * turadi.
 */
const RequisitionsView = ({ onOpen }: { onOpen?: (id: string) => void }) => {
  const [page, setPage] = useState(1);
  const [requirementFor, setRequirementFor] = useState<RequirementTarget | null>(null);
  const [confirmFor, setConfirmFor] = useState<RequisitionDto | null>(null);
  const requisitions = useMyRequisitions(page);
  const queryClient = useQueryClient();
  const { canAnywhere } = usePermission();
  const canConfirm = canAnywhere('requisition.confirm.receipt');

  if (requisitions.isPending) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (requisitions.isError) {
    return (
      <Card>
        <EmptyState
          icon={<TriangleAlert size={20} aria-hidden />}
          title={t('myStock.loadError')}
          description={requisitions.error.message}
        />
      </Card>
    );
  }

  if (requisitions.data.items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ClipboardList size={20} aria-hidden />}
          title={t('myStock.reqEmpty')}
          description={t('myStock.reqEmptyHint')}
        />
      </Card>
    );
  }

  const pages = Math.max(1, Math.ceil(requisitions.data.total / requisitions.data.limit));

  return (
    <div className="flex flex-col gap-3">
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {requisitions.data.items.map((row) => (
          <RequisitionMiniCard
            key={row.id}
            row={row}
            canConfirm={canConfirm}
            onShowRequirement={() => setRequirementFor({ id: row.id, docNo: row.docNo })}
            onConfirm={() => setConfirmFor(row)}
            {...(onOpen === undefined ? {} : { onOpen })}
          />
        ))}
      </ul>

      {/* TREBOVANIYA — buxgalter chiqargan ФМУ №25 qog'ozining O'ZI.
          Bo'lim xodimi uni ko'rishi kerak: mahsulotni qabul qilayotganda
          qo'lidagi qog'oz bilan ekrandagini solishtiradi. */}
      {requirementFor === null ? null : (
        <RequirementFor target={requirementFor} onClose={() => setRequirementFor(null)} />
      )}

      {confirmFor === null ? null : (
        <ReceiptDialog
          open
          requisitionId={confirmFor.id}
          onClose={() => setConfirmFor(null)}
          onDone={() => {
            setConfirmFor(null);
            /* Qabul BIR VAQTNING O'ZIDA uch ro'yxatni o'zgartiradi: talabnoma
               holati, bo'linma qoldig'i va jurnal. Bittasi eskirib qolsa,
               xodim mahsulotni «qabul qildim» deb turib, qoldiqda ko'rmasdi. */
            void queryClient.invalidateQueries({ queryKey: mySubdivisionKeys.root });
          }}
        />
      )}

      {pages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <span className="tabular text-xs text-text-muted">
            {t('myStock.pageOf', { page, pages })}
          </span>
          <span className="flex gap-2">
            <PageButton disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t('myStock.prev')}
            </PageButton>
            <PageButton disabled={page >= pages} onClick={() => setPage(page + 1)}>
              {t('myStock.next')}
            </PageButton>
          </span>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Talabnomaning kartochkasi — TO'LIQ hujjat emas, unga eshik.
 *
 * Bu yerda satrlar ham, tarix ham yo'q va ataylab: ular bitta talabnoma
 * uchun ham uzun ro'yxat va o'nta kartochkada ekranni butunlay bosib
 * ketardi. Kartochka «qaysi hujjat, qanday holatda, qachon» degan uch
 * savolga javob beradi, qolganini kartochka OCHADI.
 */
const RequisitionMiniCard = ({
  row,
  onOpen,
  canConfirm,
  onShowRequirement,
  onConfirm,
}: {
  row: RequisitionDto;
  onOpen?: (id: string) => void;
  /** Xodimda `requisition.confirm.receipt` bormi — qabul tugmasi shunga qarab. */
  canConfirm: boolean;
  onShowRequirement: () => void;
  onConfirm: () => void;
}) => (
  <li className="flex flex-col">
    <button
      type="button"
      disabled={onOpen === undefined}
      onClick={() => onOpen?.(row.id)}
      /* Kartochkada endi bir nechta tugma bor — bu birinchisining nima
         qilishini nomi bilan aytadi ("A-2026-0014 talabnomasini ochish"),
         aks holda ekran o'quvchi uchta nomsiz tugmani sanab chiqardi. */
      aria-label={t('myStock.openRequisition', { docNo: row.docNo })}
      className={cn(
        'flex h-full w-full flex-col gap-3 rounded-t-[var(--radius-lg)] border border-border',
        'bg-surface p-4 text-left shadow-[var(--shadow-1)]',
        'transition-[border-color] duration-[var(--motion-fast)] hover:border-border-strong',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-default disabled:hover:border-border',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="tabular text-sm font-semibold text-text">{row.docNo}</span>
        <Badge tone={requisitionStatusTone(row.status)}>
          {requisitionStatusText(row.status)}
        </Badge>
      </div>

      <p className="line-clamp-2 min-h-[2.5rem] text-sm leading-5 text-pretty text-text-muted">
        {row.purpose}
      </p>

      <dl className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2.5 text-xs text-text-muted">
        <Fact icon={<CalendarDays size={12} />} value={formatDate(row.createdAt)} />
        {/* Muddat faqat KO'RSATILGAN bo'lsa: bo'sh «—» qator kartochkani
            to'ldirardi, lekin hech narsa aytmasdi. */}
        {row.neededBy === null ? null : (
          <Fact icon={<Clock size={12} />} value={formatDate(row.neededBy)} />
        )}
        {row.throughPerson === null ? null : (
          <Fact icon={<User size={12} />} value={row.throughPerson} />
        )}
      </dl>
    </button>

    {/*
      AMALLAR kartochkaning TASHQARISIDA turadi va bu HTML talabi ham:
      tugma ichida tugma bo'lmaydi. Ma'no jihatidan ham to'g'ri — kartochka
      hujjatni OCHADI, bu ikkitasi esa u bilan ish qiladi.

      «Trebovaniya» har doim bor: qog'oz talabnomaning butun umri davomida
      kerak. «Qabul qilish» esa faqat `HANDED_OVER` da — boshqa holatda
      bosiladigan narsa yo'q va o'chiq tugma qatorni bekorga to'ldirardi.
    */}
    <div className="-mt-px flex flex-wrap gap-2 rounded-b-[var(--radius-lg)] border border-t-0 border-border bg-surface-2 px-3 py-2">
      <Button variant="ghost" size="sm" onClick={onShowRequirement}>
        <FileText size={14} aria-hidden />
        {t('myStock.reqRequirement')}
      </Button>
      {row.status === 'HANDED_OVER' && canConfirm ? (
        <Button size="sm" onClick={onConfirm}>
          <PackageCheck size={14} aria-hidden />
          {t('myStock.reqConfirm')}
        </Button>
      ) : null}
    </div>
  </li>
);

/**
 * TREBOVANIYA oynasi — HAQIQIY hujjat, topilsa.
 *
 * So'rovnoma ochilgan bo'lsa ФМУ №25 raqami bilan chiqadi; hali ochilmagan
 * bo'lsa qoralama ko'rinadi («QORALAMA» yozuvi va raqam o'rni bo'sh).
 * Ikkalasini ham ko'rsatish kerak: talabnoma yo'lda turganda ham xodim
 * nima so'ralganini qog'oz ko'rinishida ko'rmoqchi bo'ladi.
 *
 * Qaysi ko'rinish kerakligini SERVER hal qiladi — ilgari buni ekran
 * qilardi (so'rovnomani alohida so'rab) va shu tanlov imzolarni
 * yo'qotardi.
 *
 * So'rov FAQAT oyna ochilganda ketadi — har kartochka uchun oldindan
 * so'ralsa, bitta ekran o'nlab bekor so'rov yuborardi.
 */
export interface RequirementTarget {
  id: string;
  docNo: string;
}

const RequirementFor = ({
  target,
  onClose,
}: {
  target: RequirementTarget;
  onClose: () => void;
}) => (
  <RequirementDialog
    open
    /* Manba HAR DOIM talabnoma — so'rovnoma ochilgan bo'lsa ham.
       `surveys/:id/requirement.pdf` blankni chiza oladi, lekin uning IMZO
       TASMASI hech qachon to'lmaydi: kim bergani va kim qabul qilgani
       talabnoma o'tishlarida yozilgan. Mahsulotni olgan xodim esa aynan
       shu qog'ozni ochadi va «Отпустил» / «Получил» kataklari bo'sh
       chiqardi. */
    source={{ requisition: target.id }}
    docNo={target.docNo}
    onClose={onClose}
  />
);

const JournalView = ({ onOpenRequisition }: { onOpenRequisition?: (id: string) => void }) => {
  const [page, setPage] = useState(1);
  const journal = useMyMovements(page);

  if (journal.isPending) return <Skeleton className="h-64 w-full" />;

  if (journal.isError) {
    return (
      <Card>
        <EmptyState
          icon={<TriangleAlert size={20} aria-hidden />}
          title={t('myStock.loadError')}
          description={journal.error.message}
        />
      </Card>
    );
  }

  if (journal.data.items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Inbox size={20} aria-hidden />}
          title={t('myStock.journalEmpty')}
          description={t('myStock.journalEmptyHint')}
        />
      </Card>
    );
  }

  const pages = Math.max(1, Math.ceil(journal.data.total / journal.data.limit));

  return (
    <Card padded={false}>
      <ul className="flex flex-col">
        {journal.data.items.map((row) => (
          <li
            key={row.id}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3 last:border-0"
          >
            <Badge tone={MOVEMENT_TONE[row.type]}>{t(`myStock.move.${row.type}`)}</Badge>

            <span className="min-w-0 flex-1 text-sm text-text">
              {row.nomenclatureName}
              <span className="tabular ml-2 text-xs text-text-faint">{row.batchNo}</span>
            </span>

            <span className="tabular text-sm font-medium text-text">
              {qtyText(row.qty)}{' '}
              <span className="font-normal text-text-muted">{unitText(row.unit as Unit)}</span>
            </span>

            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
              {row.docNo === null ? null : (
                <RequisitionRef
                  docNo={row.docNo}
                  id={row.docId}
                  {...(onOpenRequisition === undefined || row.docType !== 'REQUISITION'
                    ? {}
                    : { onOpen: onOpenRequisition })}
                />
              )}
              {row.performedByName === null ? null : (
                <Fact icon={<User size={12} />} value={row.performedByName} />
              )}
              <span className="tabular text-text-faint">{formatMoment(row.performedAt)}</span>
            </span>

            {/* Sabab faqat bo'lganda: hisobdan chiqarish va qaytarishda u
                hujjatning eng muhim qismi bo'ladi (2-bosqich). */}
            {row.reason === null ? null : (
              <p className="w-full rounded-[var(--radius-sm)] bg-surface-2 px-2.5 py-1.5 text-xs text-text-muted">
                {row.reason}
              </p>
            )}
          </li>
        ))}
      </ul>

      {/* Sahifalash faqat KERAK bo'lganda: bitta sahifada turgan jurnal
          ostida bo'sh boshqaruv paneli "yana bor" degan taassurot qoldirardi. */}
      {pages > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
          <span className="tabular text-xs text-text-muted">
            {t('myStock.pageOf', { page, pages })}
          </span>
          <span className="flex gap-2">
            <PageButton disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t('myStock.prev')}
            </PageButton>
            <PageButton disabled={page >= pages} onClick={() => setPage(page + 1)}>
              {t('myStock.next')}
            </PageButton>
          </span>
        </div>
      ) : null}
    </Card>
  );
};

const PageButton = ({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'rounded-[var(--radius-sm)] border border-border bg-surface px-3 py-1.5 text-xs',
      'text-text transition-colors hover:border-border-strong',
      'disabled:cursor-not-allowed disabled:opacity-50',
    )}
  >
    {children}
  </button>
);
